import ErrorNote from './ErrorNote'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CalendarOff, Check, ChevronDown, FileText, Repeat } from 'lucide-react'
import { clockTime, meridiem, reasonLabel } from '../api/attendance'
import { formatFullDate, today } from '../api/calendar'
import {
  byDancer,
  byDate,
  distinctDancers,
  documentHref,
  excuseAbsence,
  excuseAbsences,
  fetchReportedAbsences,
  inWindow,
  isNew,
  markAbsencesSeen,
  matchesSearch,
  repeatedAbsences,
  stillCounting,
  withdrawExcusal,
  type AbsenceWhen,
  type DancerDay,
  type ReportedAbsence,
} from '../api/reportedAbsences'

/**
 * Who has told the studio they will be missing (D-188).
 *
 * <b>One screen, scoped rather than two</b> (D-83). A teacher sees her own
 * classes and anything she is covering; Tonya sees the studio and can excuse.
 * The server decides which — this renders whatever it is given, so there is no
 * second permission model here to drift from the first.
 *
 * <b>Never says "excused" to anyone who cannot grant it.</b> The field is
 * absent from the payload for those callers, not merely hidden, because Tonya
 * asked for a private mechanism and a field the browser receives is one anybody
 * can read.
 */
export default function ReportedAbsencesView() {
  const [when, setWhen] = useState<AbsenceWhen>('all')

  /*
   * **Finding one child's absences** (D-412). `asked`: *"this page should be
   * searchable. so if a parent comes to tonya and asks about their kids
   * absenses she should be able to pull it up and see the kids attendence."*
   *
   * In the browser, not a round trip: the endpoint already returns everything
   * (D-197), so typing is instant and the counts below stay truthful.
   */
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['reported-absences'],
    queryFn: () => fetchReportedAbsences(),
  })

  /*
   * The marker as it stood when this visit began (D-211).
   *
   * Held in state rather than read from `data` on every render, so the rows
   * that were new stay marked for the whole visit — the badge clears the
   * moment the screen opens, and a glance must not also destroy the answer to
   * "which of these am I looking at?".
   */
  const [seenAtOnArrival, setSeenAtOnArrival] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    if (!data || seenAtOnArrival !== undefined) return
    setSeenAtOnArrival(data.seenAt)
  }, [data, seenAtOnArrival])

  const stamp = useMutation({
    mutationFn: markAbsencesSeen,
    // The nav badge reads its own copy of the list, and it is now stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reported-absences', 'nav'] }),
  })

  /*
   * Recorded once the list has actually arrived, never on mount.
   *
   * Stamping before the rows load would mark absences as seen that the person
   * never had a chance to see — including the case where the request then
   * fails, which would clear the badge and show them nothing.
   */
  useEffect(() => {
    if (!data || stamp.isPending || stamp.isSuccess) return
    stamp.mutate()
  }, [data, stamp])

  // The studio's today, not the browser's (D-68).
  const now = today()

  /*
   * **Searched first, then narrowed by time.** The counts on the three time
   * options are computed from the searched set, so they answer "how many of
   * *these*" rather than "how many altogether" - narrowing must never hide
   * something silently, which is the reason those counts exist at all.
   */
  const all = (data?.absences ?? []).filter((a) => matchesSearch(a, search))
  const shown = inWindow(all, when, now)

  const days = byDate(shown, now)
  const repeats = repeatedAbsences(shown)

  const counts = {
    all: all.length,
    upcoming: inWindow(all, 'upcoming', now).length,
    past: inWindow(all, 'past', now).length,
  }

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Attendance</p>
        <h1 className="font-display text-2xl">Reported absences</h1>
        <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
          {data?.isStudioWide
            ? 'Everyone whose family has told us they will be missing, across the studio.'
            : 'Dancers in your classes whose families have told us they will be missing.'}
        </p>
      </header>

      {/*
        **A name, so a parent at the desk gets an answer** (D-412).

        Rendered whenever the screen has anything at all — keyed on the
        unsearched list, or typing a name that matches nothing would take the
        box away and strand her with no way to clear it.
      */}
      {(data?.absences.length ?? 0) > 0 && (
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a dancer or a class"
          aria-label="Search a dancer or a class"
          className="mb-3 w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-accent"
        />
      )}

      {/*
        Everything, however far ahead (D-197). A family reporting a holiday two
        months out is exactly what this screen is for, and the old fixed window
        of four weeks back and two forward hid it — the report was stored and
        invisible, which looks identical to not having been made.

        Counts on the options, so narrowing never hides something silently —
        and they count the *searched* set since D-412, or they would answer a
        question about a list she is no longer looking at.
      */}
      {all.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {(
            [
              ['all', 'Everything', counts.all],
              ['upcoming', 'Still to come', counts.upcoming],
              ['past', 'Already happened', counts.past],
            ] as const
          ).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              aria-pressed={when === key}
              onClick={() => setWhen(key)}
              className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold ${
                when === key
                  ? 'border-accent bg-accent-soft text-accent-2'
                  : 'border-line-2 text-ink-3 hover:border-ink-3'
              }`}
            >
              {label} <span className="tabular">({count})</span>
            </button>
          ))}
        </div>
      )}

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}

      {isError && (
        <ErrorNote boxed>
          {(error as Error).message}
        </ErrorNote>
      )}

      {data && shown.length === 0 && (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-5 py-6">
          <CalendarOff size={22} strokeWidth={2} className="shrink-0 text-ink-3" />
          <div>
            {/*
              **A search that found nothing is not the studio having nothing**
              (D-412). Without this branch, typing a name that does not match
              renders *"Nobody has reported an absence"* — a statement about the
              data, made by a filter. That is the shape D-209 exists to stop.
            */}
            <p className="text-[14px] font-semibold">
              {search.trim() && all.length === 0
                ? 'Nobody by that name'
                : all.length === 0
                  ? 'Nobody has reported an absence'
                  : 'Nothing in this period'}
            </p>
            <p className="mt-0.5 text-[13px] text-ink-3">
              {search.trim() && all.length === 0
                ? 'Try part of a dancer’s name, or a class.'
                : all.length === 0
                  ? 'Families report these from their own schedule, however far ahead.'
                  : 'There are others — try Everything above.'}
            </p>
          </div>
        </div>
      )}

      {shown.length > 0 && (
        <p className="mb-4 text-[13px] text-ink-3 tabular">
          {distinctDancers(shown)} {distinctDancers(shown) === 1 ? 'dancer' : 'dancers'} across{' '}
          {shown.length} {shown.length === 1 ? 'class' : 'classes'}
        </p>
      )}

      {/*
        The pattern D-122 assumed somebody would notice and gave nobody a way to
        see. A count, not a verdict — what follows is a conversation.
      */}
      {repeats.length > 0 && (
        <div className="mb-5 rounded-2xl border border-warn bg-warn-bg px-4 py-3">
          <p className="flex items-center gap-2 text-[13px] font-semibold text-warn">
            <Repeat size={14} strokeWidth={2.5} className="shrink-0" />
            Out on more than one day in this period
          </p>
          <p className="mt-1.5 text-[13px] text-ink-2">
            {repeats.map((r) => `${r.dancerName} (${r.count} days)`).join(' · ')}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {days.map(({ date, absences, isPast }, i) => (
          <section key={date}>
            {/*
              One divider where the list crosses today, so "everything" reads as
              two lists rather than one long undifferentiated one.
            */}
            {isPast && !days[i - 1]?.isPast && (
              <p className="mb-3 border-t border-line pt-4 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                Already happened
              </p>
            )}

            <h2 className="mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
              {formatFullDate(date)}
              {!isPast && date === now && <span className="ml-2 text-accent">today</span>}
            </h2>

            {/*
              One line per dancer, not per session (D-213). Ellie missing three
              classes with a fever was three cards, so ten dancers out on a busy
              evening would have been thirty rows describing ten children.
            */}
            <div className="flex flex-col gap-2.5">
              {byDancer(absences).map((day) => (
                <DancerDayCard
                  key={`${day.dancerId}|${day.date}`}
                  day={day}
                  canExcuse={data?.canExcuse ?? false}
                  seenAt={seenAtOnArrival ?? null}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * One dancer's day (D-213).
 *
 * <b>A dancer missing a single class does not collapse.</b> It renders as the
 * card always has — otherwise tidying up would add a click to the simplest
 * case, which is a bad trade for a screen somebody scans.
 *
 * <b>The excusal state stays visible while collapsed.</b> It is Tonya's
 * mechanism and the thing she is looking for; hiding it behind an expand would
 * mean opening every row to find what she already did.
 */
function DancerDayCard({
  day,
  canExcuse,
  seenAt,
}: {
  day: DancerDay
  canExcuse: boolean
  seenAt: string | null
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [excusing, setExcusing] = useState(false)

  const outstanding = stillCounting(day)
  const anythingNew = day.absences.some((a) => isNew(a, seenAt))

  const excuseAll = useMutation({
    mutationFn: () =>
      excuseAbsences(outstanding.map((a) => a.attendanceRecordId), note.trim() || null),
    onSuccess: () => {
      setExcusing(false)
      setNote('')
      queryClient.invalidateQueries({ queryKey: ['reported-absences'] })
    },
  })

  // One class is not a group. Rendering it as one would bury a single card
  // behind a disclosure triangle for no gain.
  if (day.absences.length === 1) {
    return <AbsenceCard absence={day.absences[0]} canExcuse={canExcuse} isNew={anythingNew} />
  }

  const allExcused = day.excusedCount !== null && day.excusedCount === day.absences.length

  return (
    <div
      className={`overflow-hidden rounded-2xl border ${
        allExcused ? 'border-good bg-good-bg' : 'border-line bg-surface'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2 text-[14.5px] font-semibold wrap-anywhere">
            {day.dancerName}
            {anythingNew && (
              <span className="shrink-0 rounded-md bg-accent-soft px-1.5 py-0.5 text-[10.5px] font-bold tracking-wide text-accent-2 uppercase">
                New
              </span>
            )}
          </span>

          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-ink-3">
            <span className="font-semibold text-warn">
              Missing {day.absences.length} classes
            </span>

            {/* Says both when a day has two reasons, rather than picking one. */}
            {day.reasons.length > 0 && (
              <span>{day.reasons.map(reasonLabel).join(', ')}</span>
            )}

            {day.hasDocument && (
              <span className="inline-flex items-center gap-1">
                <FileText size={11} strokeWidth={2.5} />
                Note on file
              </span>
            )}
          </span>
        </span>

        {/*
          Partly-excused is a real state and a pill cannot say it, so the count
          does. Tonya having forgiven two of three is exactly the thing she
          would otherwise have to expand the row to rediscover.
        */}
        {day.excusedCount !== null && day.excusedCount > 0 && (
          <span className="shrink-0 rounded-md bg-surface px-2 py-0.5 text-[11.5px] font-bold text-good">
            {allExcused ? 'Excused' : `${day.excusedCount} of ${day.absences.length} excused`}
          </span>
        )}

        <ChevronDown
          size={15}
          strokeWidth={2.5}
          className={`shrink-0 text-ink-3 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="flex flex-col gap-2.5 border-t border-line px-4 py-3">
          {/*
            A fever misses every class that evening, and that is one decision
            rather than three. Offered only while something is still counting —
            a day already forgiven has nothing left to forgive.
          */}
          {canExcuse && outstanding.length > 1 && (
            <div className="rounded-xl border border-line-2 bg-surface-2 px-3 py-2.5">
              {excusing ? (
                <div className="flex flex-col gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[12px] font-semibold text-ink-3">
                      Why, for your own records
                    </span>
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      autoFocus
                      placeholder="Fever of 102"
                      className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-accent"
                    />
                    <span className="text-[11.5px] text-ink-3">
                      Saved against all {outstanding.length}. Families never see this.
                    </span>
                  </label>

                  {excuseAll.isError && (
                    <p role="alert" className="text-[12.5px] text-crit">
                      {(excuseAll.error as Error).message}
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={excuseAll.isPending}
                      onClick={() => excuseAll.mutate()}
                      className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
                    >
                      {excuseAll.isPending
                        ? 'Saving...'
                        : `Don't count these ${outstanding.length}`}
                    </button>

                    <button
                      type="button"
                      onClick={() => setExcusing(false)}
                      className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setExcusing(true)}
                  className="flex items-center gap-2 text-[12.5px] font-semibold text-ink-2 hover:text-ink"
                >
                  <Check size={13} strokeWidth={2.5} />
                  Don&rsquo;t count any of these {outstanding.length}
                </button>
              )}
            </div>
          )}

          {day.absences.map((absence) => (
            <AbsenceCard
              key={absence.attendanceRecordId}
              absence={absence}
              canExcuse={canExcuse}
              isNew={isNew(absence, seenAt)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AbsenceCard({
  absence,
  canExcuse,
  isNew,
}: {
  absence: ReportedAbsence
  canExcuse: boolean
  /** Arrived since this person last opened the screen (D-211). */
  isNew: boolean
}) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const [open, setOpen] = useState(false)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['reported-absences'] })

  const excuse = useMutation({
    mutationFn: () => excuseAbsence(absence.attendanceRecordId, note.trim() || null),
    onSuccess: () => {
      setOpen(false)
      setNote('')
      refresh()
    },
  })

  const undo = useMutation({
    mutationFn: () => withdrawExcusal(absence.attendanceRecordId),
    onSuccess: refresh,
  })

  const reason = reasonLabel(absence.reason)

  return (
    <div
      className={`rounded-2xl border px-4 py-3 ${
        absence.isExcused ? 'border-good bg-good-bg' : 'border-line bg-surface'
      }`}
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 text-[14.5px] font-semibold wrap-anywhere">
            {absence.dancerName}
            {/*
              Survives the whole visit, because the badge clears the moment the
              screen opens (D-211). Glancing at the page must not also destroy
              the answer to "which of these am I actually looking at?".
            */}
            {isNew && (
              <span className="shrink-0 rounded-md bg-accent-soft px-1.5 py-0.5 text-[10.5px] font-bold tracking-wide text-accent-2 uppercase">
                New
              </span>
            )}
          </p>

          {/* Class names are machine-shaped and do not wrap on their own. */}
          <p className="mt-0.5 text-[12.5px] wrap-anywhere text-ink-3">
            {absence.startTime && (
              <>
                <span className="tabular">{clockTime(absence.startTime)}</span>
                {meridiem(absence.startTime)} &middot;{' '}
              </>
            )}
            {absence.className}
          </p>
        </div>

        {/*
          Only ever rendered when the server sent the field, which it does only
          for a caller who may excuse (D-188).
        */}
        {absence.isExcused === true && (
          <span className="shrink-0 rounded-md bg-surface px-2 py-0.5 text-[11.5px] font-bold text-good">
            Excused
          </span>
        )}
      </div>

      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] font-semibold text-warn">
        <AlertCircle size={13} strokeWidth={2.5} className="shrink-0" />
        Reported out
        {reason && <span className="font-normal text-ink-2">{reason}</span>}
        {absence.reportedBy && (
          <span className="font-normal text-ink-3">by {absence.reportedBy}</span>
        )}
      </p>

      {absence.reportedNote && (
        <p className="mt-1 text-[12.5px] wrap-anywhere text-ink-2">
          &ldquo;{absence.reportedNote}&rdquo;
        </p>
      )}

      {/*
        Openable for whoever may read one, a plain indicator for everyone else
        (D-9, D-213). The id only reaches browsers holding the capability, so
        this is not the UI deciding — and the server audits the read before it
        serves the file.

        Downloads rather than opening inline: a child's medical note should not
        render in a browser tab because somebody clicked a row.
      */}
      {absence.hasDocument &&
        (absence.documentId ? (
          <a
            href={documentHref(absence.documentId)}
            className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-semibold text-accent-2 hover:underline"
          >
            <FileText size={12} strokeWidth={2.5} className="shrink-0" />
            Open the note
          </a>
        ) : (
          <p className="mt-1 flex items-center gap-1.5 text-[12px] text-ink-3">
            <FileText size={12} strokeWidth={2.5} className="shrink-0" />
            Note on file
          </p>
        ))}

      {absence.excusedNote && (
        <p className="mt-1.5 text-[12px] wrap-anywhere text-good">{absence.excusedNote}</p>
      )}

      {canExcuse && (
        <div className="mt-2.5">
          {absence.isExcused ? (
            <button
              type="button"
              onClick={() => undo.mutate()}
              disabled={undo.isPending}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink disabled:opacity-50"
            >
              {undo.isPending ? 'Undoing...' : 'Put it back in the count'}
            </button>
          ) : open ? (
            <div className="flex flex-col gap-2">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why, for your own reference (optional)"
                autoFocus
                className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] outline-none focus:border-accent"
              />

              {/* Said plainly, because this is the whole effect of the button. */}
              <p className="text-[12px] text-ink-3">
                This stops the session counting toward her attendance. Only people you have given
                permission can see it.
              </p>

              {excuse.isError && (
                <p role="alert" className="text-[12.5px] text-crit">
                  {(excuse.error as Error).message}
                </p>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => excuse.mutate()}
                  disabled={excuse.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
                >
                  <Check size={13} strokeWidth={3} />
                  {excuse.isPending ? 'Saving...' : "Don't count this"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    setNote('')
                  }}
                  className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
            >
              Don&rsquo;t count this one
            </button>
          )}
        </div>
      )}
    </div>
  )
}
